import React from "react";
import Layout from "./Layout";
import { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom/dist";
import { addDoc, collection, doc, getDoc, updateDoc } from "firebase/firestore";
import { db, storage } from "../../../config/firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

import {
  createInitialForm,
  fetchLatestId,
  updateLatestId,
  validateRequiredField,
} from "../../../common/common";
import { campusData } from "../../../assets/lib/campus";
import { COLLECTION, COLLECTION_COUNTER } from "../../../data/constant";
import { routes } from "../../../routing/routes";

const CampusInfo = () => {
  const { campusDocId } = useParams();
  const navigate = useNavigate();
  const [isEdit, setIsEdit] = useState(false);
  const [isNewAdd, setIsNewAdd] = useState(true);
  const { state: locationState } = useLocation();
  const [campusState, setCampusState] = useState({
    ...createInitialForm(campusData),
    campusAdmins: [{ email: "", password: "", disabled: false }]
  });

  const [errorFields, setErrorFields] = useState([]);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [errorOnFetch, setErrorOnFetch] = useState(false);

  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadedImage, setUploadedImage] = useState(null);

  const getCampusInput = (event) => {
    if (!isEdit && !isNewAdd) return;
    const name = event.target.name;
    let value = event.target.value;

    if (value && event.target.type === "number") value = parseInt(value);

    if (errorFields.includes(name)) {
      setErrorFields((prev) => prev.filter((key) => key !== name));
    }

    setCampusState((prev) => ({ ...prev, [name]: value }));
  };

  const updateAdminField = (index, field, value) => {
    const updated = [...campusState.campusAdmins];
    updated[index][field] = value;
    setCampusState((prev) => ({ ...prev, campusAdmins: updated }));
  };

  const addAdmin = () => {
    setCampusState((prev) => ({
      ...prev,
      campusAdmins: [...prev.campusAdmins, { email: "", password: "", disabled: false }]
    }));
  };

  const removeAdmin = (index) => {
    const updated = [...campusState.campusAdmins];
    updated.splice(index, 1);
    setCampusState((prev) => ({ ...prev, campusAdmins: updated }));
  };

  const toggleDisableAdmin = (index) => {
    const updated = [...campusState.campusAdmins];
    updated[index].disabled = !updated[index].disabled;
    setCampusState((prev) => ({ ...prev, campusAdmins: updated }));
  };

  const handleSubmitModal = async () => {
    const error = validateRequiredField(campusData, campusState);
    setErrorFields(error);
    setSubmitLoading(true);

    if (error.length === 0) {
      try {
        let fileUrl = "";
        if (uploadedFile) {
          const storageRef = ref(storage, `Documents/${Date.now()}_${uploadedFile.name}`);
          await uploadBytes(storageRef, uploadedFile);
          fileUrl = await getDownloadURL(storageRef);
          campusState.documentUrl = fileUrl;
        }

        let imageUrl = "";
        if (uploadedImage) {
          const storageRef = ref(storage, `Images/${Date.now()}_${uploadedImage.name}`);
          await uploadBytes(storageRef, uploadedImage);
          imageUrl = await getDownloadURL(storageRef);
          campusState.imageUrl = imageUrl;
        }

        delete campusState.email;
        delete campusState.password;

        if (isEdit && campusDocId) {
          const docRef = doc(db, COLLECTION.CAMPUS, campusDocId);
          await updateDoc(docRef, campusState);
        } else {
          const campusCollectionRef = collection(db, COLLECTION.CAMPUS);
          await addDoc(campusCollectionRef, campusState);
          await updateLatestId(COLLECTION_COUNTER.CAMPUS, campusState.campusId);
        }

        setSubmitLoading(false);
        navigate(routes.CAMPUS_MANAGEMENT);
      } catch (error) {
        setSubmitLoading(false);
        setErrorOnFetch(true);
      }
    }
  };

  useEffect(() => {
    setIsEdit(locationState?.editCampus || false);
    setIsNewAdd(locationState?.addCampus || false);

    const fetchingInfo = async () => {
      setFetchLoading(true);
      try {
        if (locationState?.addCampus) {
          const latestId = await fetchLatestId(COLLECTION_COUNTER.CAMPUS);
          setCampusState((prev) => ({ ...prev, [campusData.campusId.key]: latestId }));
        } else {
          const docRef = doc(db, COLLECTION.CAMPUS, campusDocId);
          const docSnap = await getDoc(docRef);

          if (docSnap.exists()) {
            const storedData = docSnap.data();
            setCampusState((prev) => ({
              ...prev,
              ...storedData,
              campusAdmins: storedData.campusAdmins || [{ email: "", password: "", disabled: false }]
            }));
          } else {
            throw new Error("Campus ID does not exist");
          }
        }
      } catch (error) {
        setErrorOnFetch(true);
      }
      setFetchLoading(false);
    };
    fetchingInfo();
  }, []);

  return (
    <Layout
      isNewAdd={isNewAdd}
      isEdit={isEdit}
      setIsEdit={setIsEdit}
      getCampusInput={getCampusInput}
      updateAdminField={updateAdminField}
      addAdmin={addAdmin}
      removeAdmin={removeAdmin}
      toggleDisableAdmin={toggleDisableAdmin}
      campusState={campusState}
      handleSubmitModal={handleSubmitModal}
      errorFields={errorFields}
      fetchLoading={fetchLoading}
      submitLoading={submitLoading}
      errorOnFetch={errorOnFetch}
      onFileUpload={setUploadedFile}
      onImageUpload={setUploadedImage}
    />
  );
};

export default CampusInfo;
